import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  resolvePath,
  useLocation,
  useNavigate,
  type NavigateOptions,
  type To,
} from "react-router-dom";

/** Answers whether it is safe to navigate away — prompting the user if it has to. */
export type NavigationGuard = () => Promise<boolean>;

interface NavigationGuardContextValue {
  /** Call on mount; the returned cleanup unregisters. */
  registerGuard: (guard: NavigationGuard) => () => void;
  /** Asks every registered guard in turn; false means the navigation was refused. */
  confirmNavigation: () => Promise<boolean>;
}

/**
 * Lets a page with unsaved edits stop a navigation the chrome around it starts.
 *
 * This router is a plain <Routes> tree rather than a data router, so React
 * Router's own useBlocker is not available: the links ask instead. That means a
 * guard only covers what has been wired to it — the header nav, the board
 * switcher and the brand link — and not the browser's own Back button or a
 * reload.
 *
 * The default value navigates without asking, so a nav link rendered outside
 * the provider (a story, a unit test) still works; an unguarded link is the
 * behaviour everything had before this existed.
 */
const NavigationGuardContext = createContext<NavigationGuardContextValue>({
  registerGuard: () => () => {},
  confirmNavigation: () => Promise.resolve(true),
});

export const NavigationGuardProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // A ref, not state: registering a guard must not re-render the whole layout,
  // and confirmNavigation reads it at click time rather than at render time.
  const guardsRef = useRef<Set<NavigationGuard>>(new Set());

  const registerGuard = useCallback((guard: NavigationGuard) => {
    guardsRef.current.add(guard);
    return () => {
      guardsRef.current.delete(guard);
    };
  }, []);

  const confirmNavigation = useCallback(async () => {
    // Sequentially, over a copy: a guard prompts, and two dialogs at once would
    // be unanswerable. The first refusal stops the rest from asking at all.
    for (const guard of [...guardsRef.current]) {
      if (!(await guard())) {
        return false;
      }
    }
    return true;
  }, []);

  const value = useMemo<NavigationGuardContextValue>(
    () => ({ registerGuard, confirmNavigation }),
    [registerGuard, confirmNavigation],
  );

  return (
    <NavigationGuardContext.Provider value={value}>
      {children}
    </NavigationGuardContext.Provider>
  );
};

/**
 * Registers a guard for as long as the calling component is mounted.
 *
 * The guard is read through a ref, so a page may pass a fresh closure on every
 * render without re-registering.
 */
export function useNavigationGuard(guard: NavigationGuard): void {
  const { registerGuard } = useContext(NavigationGuardContext);
  const guardRef = useRef(guard);
  useLayoutEffect(() => {
    guardRef.current = guard;
  });

  useLayoutEffect(
    () => registerGuard(() => guardRef.current()),
    [registerGuard],
  );
}

/** navigate(), but it asks the registered guards first and stays put if one refuses. */
export function useGuardedNavigate(): (
  to: To,
  options?: NavigateOptions,
) => void {
  const { confirmNavigation } = useContext(NavigationGuardContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return useCallback(
    (to: To, options?: NavigateOptions) => {
      // Clicking the tab you are already on leaves nothing behind, so it is not
      // worth a prompt about unsaved work.
      if (resolvePath(to, pathname).pathname === pathname) {
        navigate(to, options);
        return;
      }

      void (async () => {
        if (await confirmNavigation()) {
          navigate(to, options);
        }
      })();
    },
    [confirmNavigation, navigate, pathname],
  );
}

/**
 * onClick for a <Link> or <NavLink> that a guard may refuse.
 *
 * Modified clicks are left alone: ctrl/cmd-click opens a new tab, which leaves
 * the guarded page exactly where it is and so has nothing to ask about.
 */
export function useGuardedLinkClick(): (
  to: To,
) => React.MouseEventHandler<HTMLAnchorElement> {
  const guardedNavigate = useGuardedNavigate();

  return useCallback(
    (to: To) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.currentTarget.target === "_blank"
      ) {
        return;
      }

      event.preventDefault();
      guardedNavigate(to);
    },
    [guardedNavigate],
  );
}
